import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import styles from './DashboardChart.module.css';

const DashboardChart = ({ clients = [], transactions = [], otherRevenues = [] }) => {
  const [viewMode, setViewMode] = useState('top_clients');

  const chartData = useMemo(() => {
    switch (viewMode) {
      case 'top_clients': {
        return [...clients]
          .map(c => ({
            name: c.clientName || c.name || 'Unknown',
            inrAmount: parseFloat(c.totalAmount || c.remainingAmount || 0),
            totalNum: parseFloat(c.totalAmount || c.remainingAmount || 0)
          }))
          .sort((a, b) => b.totalNum - a.totalNum)
          .slice(0, 5);
      }
      case 'revenue_timeline': {
        const aggregated = {};
        const clientTxMap = {};
        
        [...transactions, ...otherRevenues].forEach(item => {
          if (item.clientId) clientTxMap[item.clientId] = true;
          const d = new Date(item.date).toLocaleDateString();
          if (!aggregated[d]) aggregated[d] = 0;
          aggregated[d] += (parseFloat(item.amount) || 0);
        });

        clients.forEach(c => {
          if (!clientTxMap[c.id]) {
            const amt = parseFloat(c.receivedAmount || 0);
            if (!isNaN(amt) && amt > 0) {
              const d = new Date(c.lastPaymentDate || c.createdAt || c.startDate).toLocaleDateString();
              if (!aggregated[d]) aggregated[d] = 0;
              aggregated[d] += amt;
            }
          }
        });

        return Object.entries(aggregated)
          .map(([date, amount]) => ({ date, amount }))
          .sort((a, b) => new Date(a.date) - new Date(b.date));
      }
      case 'payments_due': {
        return [...clients]
          .filter(c => parseFloat(c.remainingAmount || 0) > 0)
          .map(c => ({
            name: c.clientName || c.name || 'Unknown',
            dueAmount: parseFloat(c.remainingAmount || 0)
          }))
          .sort((a, b) => b.dueAmount - a.dueAmount);
      }
      case 'onboarding': {
        const aggregated = {};
        clients.forEach(c => {
          const d = new Date(c.startDate || c.createdAt).toLocaleDateString();
          if (!aggregated[d]) aggregated[d] = 0;
          aggregated[d] += 1;
        });
        return Object.entries(aggregated)
          .map(([date, count]) => ({ date, count }))
          .sort((a, b) => new Date(a.date) - new Date(b.date));
      }
      default:
        return [];
    }
  }, [viewMode, clients, transactions, otherRevenues]);

  const renderChart = () => {
    switch (viewMode) {
      case 'revenue_timeline':
        return (
          <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
            <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
            <Area type="monotone" dataKey="amount" name="Revenue Received (₹)" stroke="#3b82f6" fill="#eff6ff" />
          </AreaChart>
        );
      case 'payments_due':
        return (
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
            <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} cursor={{ fill: '#f1f5f9' }} />
            <Bar dataKey="dueAmount" name="Pending Amount (₹)" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={50} />
          </BarChart>
        );
      case 'onboarding':
        return (
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} allowDecimals={false} />
            <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
            <Line type="monotone" dataKey="count" name="New Clients" stroke="#10b981" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
          </LineChart>
        );
      case 'top_clients':
      default:
        return (
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
            <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} cursor={{ fill: '#f1f5f9' }} />
            <Bar dataKey="inrAmount" name="Total Revenue (₹)" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={50} />
          </BarChart>
        );
    }
  };

  return (
    <div className={styles.chartContainer}>
      <div className={styles.chartHeader}>
        <h3 className={styles.chartTitle}>Business Analytics</h3>
        <select 
          className={styles.viewSelect}
          value={viewMode} 
          onChange={(e) => setViewMode(e.target.value)}
        >
          <option value="top_clients">Top Clients (Revenue)</option>
          <option value="revenue_timeline">Revenue Timeline</option>
          <option value="payments_due">Payments Due</option>
          <option value="onboarding">Client Onboarding</option>
        </select>
      </div>
      <div className={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height={300}>
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DashboardChart;
